/* eslint-disable react/no-unescaped-entities */
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Image from 'next/image';

import ButtonLink from '../../components/atoms/button-link/button-link';
import LoadingIcon from '../../components/atoms/loading-icon/loading-icon';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import { MetaPages } from '../../enums/pages';
import SocialMediaShareSection from '../../components/molecules/social-media-share-section/social-media-share-section';
import { getFeaturedFriendsOfTheKitchenPageDetails } from '../../services/inspirations-and-planning';
import { FriendsOfTheKitchenPageDetailsType } from '../../types/content';
import Breadcrumbs from '../../components/atoms/breadcrumbs/breadcrumbs';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import MarginSpacing from '../../components/atoms/margin-spacing/margin-spacing';
import FeaturesSidebar from '../../components/molecules/features-sidebar/features-sidebar';
import { pageRoute } from '../../config/routes/pages';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import { getFeaturedNewsAndEvents } from '../../services/news';
import { Bitrix24Form } from '../../components/atoms/bitrix/bitrix';
import { strapiAssetBaseUrl } from '../../config/routes/assets';

const Container = styled.div`
  p {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    margin: 20px 0;
    text-align: justify;
  }

  caption {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    font-style: italic;
  }

  em {
    font-size: 12px;
  }

  p strong {
    font-family: MuseoSans-500;
  }

  ul {
    list-style-type: circle;
    padding-left: 40px;
  }

  ol {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    list-style-type: decimal;
    padding-left: 40px;
  }

  ol ol {
    list-style-type: lower-alpha;
  }

  li {
    margin: 8px 0;
  }

  h1 {
    font-family: ChronicleDisplayLight;
    font-size: 35px;
    font-weight: 300 !important;
    text-transform: uppercase;
    line-height: 2.25rem;
  }

  h2 {
    font-family: ChronicleDisplayLight;
    font-size: 24px;
    font-weight: 300 !important;
    text-transform: uppercase;
    line-height: 1.75rem;
    margin-top: 20px;
  }

  a {
    text-decoration-line: underline;
  }

  a:hover {
    color: #0081c6;
  }
`;

type IndexProps = {
  featuredFriendsOfTheKitchenPageDetails: FriendsOfTheKitchenPageDetailsType[];
  featuredNewsAndEvents: NewsAndEventsModel[];
  newsAndEventsArticle: NewsAndEventsModel;
};

function ArticleContent() {
  return (
    <article className="flex flex-col gap-5">
      <div>
        <h1>
          Celebrating 80 Years of Innovation. Made for Your Dream Kitchen.
        </h1>
        <p>
          A dream kitchen begins with a vision and the right appliances bring
          that vision to life.
        </p>
        <p>
          Whether you are building a new home or creating your ideal kitchen,
          discover the Sub-Zero & Wolf difference and design a space where
          exceptional preservation, cooking performance, and timeless design
          come together.
        </p>
        <p>
          For 80 years, Sub-Zero and Wolf have been redefining what is possible
          in the kitchen. From pioneering food preservation to creating
          exceptional cooking appliances, our commitment has always been to
          uncompromising performance, enduring craftsmanship, and thoughtful
          design. Today, that legacy continues in kitchens designed not only to
          perform beautifully, but to become part of the way you live.
        </p>
        <p>
          To celebrate 80 years of innovation, Sub-Zero and Wolf are pleased to
          offer an exclusive Anniversary Rebate of up to ₱85,000 for qualifying
          purchases.
        </p>

        <Image
          alt="Rebate Poster"
          height={1400}
          src={`${strapiAssetBaseUrl}/FINAL_POSTER_Rebate_Program_21x28inches_Option2_61d13fdcfd.jpg`}
          width={1000}
        />

        <p>
          Complete your Sub-Zero and Wolf kitchen and enjoy up to ₱85,000 in
          Anniversary Rebate when your purchase include 1) a full-size Sub-Zero
          Refrigerator, 2) and either a qualifying Wolf Range or a Wolf Wall
          Oven paired with a 30" or 36" Wolf Cooktop or Rangetop, 3) plus any
          two additional Sub-Zero or Wolf appliances.
        </p>
        <p>
          <em>
            * Full-size Sub-Zero Refrigeration refers to Tall units with both a
            refrigerator and freezer. For Column units, a refrigerator and
            freezer pair must be purchased. Undercounter and Wine Storage units
            are excluded.
          </em>
        </p>
        <p>
          <em>
            ** Wolf Wall Ovens include Built-in Ovens, Convection Steam Ovens,
            and Speed Ovens.
          </em>
        </p>
      </div>

      <div>
        <h2>Terms and Conditions</h2>
        <ol>
          <li>
            Get a P85,000 REBATE when you purchase the following:
            <ol>
              <li>Any full-size Sub-Zero Refrigeration*</li>
              <li>
                Either (A) Any Wall Oven** and any 30" or 36" Cooktop or
                Rangetop from Wolf or (B) any size Wolf Range
              </li>
              <li>Any 2 Sub-Zero or Wolf appliances of your choice</li>
            </ol>
            <p>
              <em>
                *Note: Any full-size refrigeration would be tall units with both
                a refrigerator and freezer (excluding Undercounter and Wine
                Storage Units). For Column units, clients have to purchase a
                pair for it to be counted as a full-size refrigeration.
              </em>
            </p>
            <p>
              <em>
                **Note: Any wall oven includes Built-in Ovens, Convection Steam
                Ovens, and Speed Ovens.
              </em>
            </p>
          </li>
          <li>
            Product count will not include sale items (i.e. red tag items, etc.)
            and accessories (i.e. ventilation blowers, trim kits, bakestones).
          </li>
          <li>
            This promo offer is valid for purchases with payment from September
            1 to December 15, 2026 only.
          </li>
          <li>
            Items purchased from this program must be delivered and installed in
            1 address.
          </li>
          <li>Full Payment is required upon placement of order.</li>
          <li>All sales are final. Cancellations will not be accepted.</li>
        </ol>
      </div>

      <div>
        <h2>Rebate Amount Availment</h2>
        <ol>
          <li>
            The rebate amount can be used as credit to make a new purchase of
            any Home Furnishings products under Focus Global (Sub-Zero, Wolf,
            Miele, SieMatic, Royana, Boffi, ADL, Orama, B&B Italia, Louis
            Poulsen, Flos, Fendi Casa, Ethan Allen, Tempur, Sealy, Villeroy &
            Boch, Dornbracht, and Hunter Douglas). All purchases must be made in
            a Focus Global showroom (Taguig, Mandaluyong, Makati, Cebu)
            excluding mall-based stores.
          </li>
          <li>
            The rebate amount may be used across multiple Home Furnishing
            brands, provided that the full voucher value is redeemed in a single
            transaction. Partial redemption is not allowed.
          </li>
          <li>
            Rebate amount should be consumed within 1 year of the date the
            amount is issued.
          </li>
          <li>
            Pick up of items using the rebate amount will only be applicable to
            weekdays and non-holidays unless there is prior approval from Focus
            Global Inc.
          </li>
          <li>
            The rebate amount is not convertible to cash, and no balance amount
            will be returned in cash.
          </li>
        </ol>
      </div>
    </article>
  );
}

export function Index(props: IndexProps) {
  const {
    featuredFriendsOfTheKitchenPageDetails,
    featuredNewsAndEvents,
    newsAndEventsArticle
  } = props;

  const router = useRouter();

  if (router.isFallback) {
    return <LoadingIcon />;
  }

  const breadcrumbs = {
    route: newsAndEventsArticle.attributes.slug,
    tabLabel: newsAndEventsArticle.attributes.news_title
  };

  const newsAndEvents = {
    route: pageRoute.NEWS_AND_EVENTS,
    tabLabel: 'NEWS AND EVENTS'
  };

  return (
    <>
      <TabTitle page={MetaPages.NEWS_AND_EVENTS} />
      <ContainerWithSidebar
        featuredContents={
          <FeaturesSidebar
            friendsOfTheKitchenPageDetails={
              featuredFriendsOfTheKitchenPageDetails
            }
            newsAndEventsContent={featuredNewsAndEvents}
          />
        }
      >
        <Breadcrumbs
          color="text-breadcrumbGray"
          subTab={breadcrumbs}
          tab={newsAndEvents}
        />
        <Container>
          <div className="mt-0 sm:mt-10" />
          <ArticleContent />
        </Container>
        <div className="mt-10">
          <Bitrix24Form
            src="https://cdn.bitrix24.com/b26420757/crm/form/loader_358.js"
            data="inline/358/ohlm2b"
          />
        </div>
        <Container>
          <div className="flex justify-end ml-auto mt-10">
            <ButtonLink
              buttonTitle="BACK"
              ctaLink={pageRoute.NEWS_AND_EVENTS}
            />
          </div>
          <MarginSpacing margin="mb-20" />
        </Container>
      </ContainerWithSidebar>
      <MarginSpacing margin="mb-5" />
      <SocialMediaShareSection />
    </>
  );
}

export async function getStaticProps() {
  const featuredFriendsOfTheKitchenPageDetails =
    await getFeaturedFriendsOfTheKitchenPageDetails();
  const featuredNewsAndEvents = await getFeaturedNewsAndEvents();
  const newsAndEventsArticle = {
    attributes: {
      slug: '090126',
      news_title:
        'Celebrating 80 Years of Innovation. Made for Your Dream Kitchen.'
    }
  } as NewsAndEventsModel;

  return {
    props: {
      featuredFriendsOfTheKitchenPageDetails,
      featuredNewsAndEvents,
      newsAndEventsArticle
    },
    revalidate: 300
  };
}

export default Index;
